import React, { useEffect, useState } from 'react';
import { capitalizeName } from '../lib/names';
import { MAX_SIGN_NAME, signNameProblem, signFontScale, tidyName, fetchMeetingPoint } from '../lib/meetGreet';

// The name sign, drawn as a real tablet held up for the arrivals hall: a rounded device frame with a thin bezel, a tiny camera dot, a glass reflection and a shadow;
// the name is large and white on the black screen under a RideBuraq orange bar.
export function SignPreview({ name }) {
  const shown = capitalizeName(tidyName(name)) || 'Your name';
  return (
    <div data-testid="sign-preview" className="w-full max-w-md mx-auto py-3">
      <div data-testid="tablet-frame" className="relative rounded-[30px] p-[11px]"
        style={{ background: 'linear-gradient(145deg, #2b2d33 0%, #131417 46%, #1f2126 100%)', boxShadow: '0 28px 46px -14px rgba(0,0,0,0.9), 0 10px 18px -8px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.16), inset 0 0 0 1px rgba(255,255,255,0.07)' }}>
        <span data-testid="tablet-camera" aria-hidden="true" className="absolute left-1/2 -translate-x-1/2 top-[3.5px] w-[5px] h-[5px] rounded-full" style={{ background: 'radial-gradient(circle at 35% 35%, #4b5160, #0a0b0d 70%)', boxShadow: '0 0 0 1px rgba(255,255,255,0.08)' }} />
        <span aria-hidden="true" className="absolute right-[-2.5px] top-[28%] w-[3px] h-9 rounded-r-sm bg-[#2a2c32]" />
        <div className="relative rounded-[20px] bg-black aspect-[16/10] w-full flex flex-col items-center justify-center px-6 overflow-hidden" style={{ boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.05)' }}>
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-orange-500" />
          <div className="text-center font-bold text-white leading-tight break-words px-2" style={{ fontSize: `${2.4 * signFontScale(name)}rem`, color: '#ffffff', position: 'relative', zIndex: 1 }} data-testid="sign-name">{shown}</div>
          <div className="absolute bottom-3 text-[10px] tracking-[0.3em] uppercase text-orange-400">RideBuraq</div>
          <div data-testid="tablet-glass" aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(115deg, rgba(255,255,255,0.13) 0%, rgba(255,255,255,0.05) 24%, rgba(255,255,255,0) 40%, rgba(255,255,255,0) 70%, rgba(255,255,255,0.04) 100%)' }} />
        </div>
      </div>
    </div>
  );
}

// What shows once "Meet & greet" is chosen: the editable "Name on sign", the live sign, and the meeting point for the airport + terminal.
// `signName` null means "use the default" (the rider's name, or the passenger's name when someone else is riding).
export default function MeetGreetPanel({ lat, lng, terminal, flightNumber, flightDate, defaultName, signName, onSignName, calm = false }) {
  const base = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('rideburaq_token');
  const [point, setPoint] = useState(null);
  const [touched, setTouched] = useState(false);
  const shownName = signName == null ? (defaultName || '') : signName;
  const problem = touched ? signNameProblem(shownName) : '';

  useEffect(() => {
    if (lat == null || lng == null) { setPoint(null); return undefined; }
    let alive = true;
    const t = setTimeout(() => {
      fetchMeetingPoint(base, token, { lat, lng, terminal, flightNumber, date: flightDate })
        .then(p => { if (alive) setPoint(p); }).catch(() => {});
    }, 400);
    return () => { alive = false; clearTimeout(t); };
  }, [base, token, lat, lng, terminal, flightNumber, flightDate]);

  return (
    <div data-testid="meet-greet" className="mt-3 space-y-4">
      <div>
        <label className={calm ? 'text-base text-gray-100' : 'text-sm text-gray-300'} htmlFor="sign-name-input">Name on sign</label>
        <input id="sign-name-input" data-testid="sign-name-input" value={shownName} maxLength={MAX_SIGN_NAME + 20}
          onChange={e => { setTouched(true); onSignName(e.target.value); }}
          placeholder="Name your chauffeur will look for"
          className={calm ? 'mt-2 w-full rounded-2xl bg-white/[0.04] border border-white/15 px-5 h-16 text-lg text-gray-50 placeholder-gray-400 focus:border-white/70 focus:outline-none' : 'mt-1 w-full rounded-xl bg-white/[0.03] border border-white/10 px-4 h-12 text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none'} />
        <div className="flex justify-between text-xs mt-1">
          <span className={calm ? 'text-red-300' : 'text-red-400'} data-testid="sign-name-error">{problem}</span>
          <span className={calm ? 'text-gray-300' : 'text-gray-500'}>{tidyName(shownName).length}/{MAX_SIGN_NAME}</span>
        </div>
      </div>
      <SignPreview name={shownName} />
      {point && (
        <div data-testid="meeting-point" className={calm ? 'rounded-2xl border border-white/15 bg-white/[0.04] px-5 py-4' : 'rounded-xl border border-orange-500/20 bg-orange-500/[0.06] px-4 py-3'}>
          <div className={calm ? 'text-sm tracking-[0.12em] uppercase text-gray-300' : 'text-xs tracking-[0.15em] uppercase text-orange-400/80'}>Meeting point · {point.terminalName || point.airportName}</div>
          <p className={calm ? 'text-base text-gray-100 mt-1' : 'text-sm text-orange-100/90 mt-1'}>{point.meetingPoint}</p>
          {point.terminalSource === 'flight' && <p className={calm ? 'text-sm text-gray-300 mt-2' : 'text-xs text-gray-500 mt-2'}>Terminal taken from your flight.</p>}
        </div>
      )}
    </div>
  );
}
